import { useEffect, useMemo, useState, useCallback } from "react";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import useBookings from "../../hooks/useBookings";
import EnhancedCreateBookingModal from "./EnhancedCreateBookingModal";
import EnhancedBookingDetailModal from "./EnhancedBookingDetailModal";
import { fetchServices } from "../../api/services";
import { formatUSD } from "../../utils/money";
import { startOfWeek, addDays, iso } from "../../utils/dates";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek as dfStartOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => dfStartOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

// Force all date/time rendering inside react-big-calendar to Eastern Time so
// booking events never drift when admins view the calendar from another zone.
const ET_TZ = "America/New_York";
const calendarFormats = {
  timeGutterFormat: (date) =>
    date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    }),
  eventTimeRangeFormat: ({ start, end }) =>
    `${start.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    })} – ${end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    })}`,
  eventTimeRangeStartFormat: ({ start }) =>
    start.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    }),
  eventTimeRangeEndFormat: ({ end }) =>
    end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    }),
  agendaTimeFormat: (date) =>
    date.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    }),
  agendaTimeRangeFormat: ({ start, end }) =>
    `${start.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    })} – ${end.toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: ET_TZ,
    })}`,
  dayFormat: (date) =>
    date.toLocaleDateString([], {
      weekday: "short",
      month: "numeric",
      day: "numeric",
      timeZone: ET_TZ,
    }),
  dayHeaderFormat: (date) =>
    date.toLocaleDateString([], {
      weekday: "long",
      month: "long",
      day: "numeric",
      timeZone: ET_TZ,
    }),
  dayRangeHeaderFormat: ({ start, end }) =>
    `${start.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      timeZone: ET_TZ,
    })} – ${end.toLocaleDateString([], {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: ET_TZ,
    })}`,
  agendaDateFormat: (date) =>
    date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: ET_TZ,
    }),
  monthHeaderFormat: (date) =>
    date.toLocaleDateString([], {
      month: "long",
      year: "numeric",
      timeZone: ET_TZ,
    }),
  weekdayFormat: (date) =>
    date.toLocaleDateString([], { weekday: "short", timeZone: ET_TZ }),
};

function parseDateOnlyToLocal(dateStr) {
  // YYYY-MM-DD -> local midnight
  const [y, m, d] = dateStr.split("-").map((x) => parseInt(x, 10));
  const dt = new Date(y, m - 1, d);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

function safeDate(v) {
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function eventClassFromStatus(status) {
  if (status === "confirmed") return "bb-event bb-confirmed";
  if (status === "canceled") return "bb-event bb-canceled";
  return "bb-event bb-pending";
}

export default function AdminCalendarWeek({
  view = "month", // month | week | day
  statusFilter = "all",
  serviceFilter = "all",
  jumpDate,
  triggerNewBooking = false,
  onServiceOptions,
  onNewBookingHandled,
  onEventsUpdate,
}) {
  const [anchor, setAnchor] = useState(() => new Date());
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [defaultStart, setDefaultStart] = useState(new Date());

  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);

  // Internal calendar view: month/week/day
  const [calView, setCalView] = useState(() => {
    if (view === "day") return "day";
    if (view === "week") return "week";
    return "month";
  });

  // keep internal calendar view in sync with page-level view
  useEffect(() => {
    if (view === "day") setCalView("day");
    else if (view === "week") setCalView("week");
    else setCalView("month");
  }, [view]);

  useEffect(() => {
    if (!jumpDate) return;
    setAnchor(parseDateOnlyToLocal(jumpDate));
  }, [jumpDate]);

  // Handle new booking trigger from sidebar
  useEffect(() => {
    if (triggerNewBooking) {
      setDefaultStart(new Date());
      setCreateOpen(true);
      onNewBookingHandled?.();
    }
  }, [triggerNewBooking, onNewBookingHandled]);

  useEffect(() => {
    (async () => {
      setLoadingServices(true);
      try {
        const data = await fetchServices();
        const list = Array.isArray(data) ? data : [];
        list.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
        setServices(list);
        onServiceOptions?.(list.map((s) => String(s.id)));
      } catch {
        // non-fatal
      } finally {
        setLoadingServices(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const serviceById = useMemo(() => {
    const m = new Map();
    for (const s of services) m.set(String(s.id), s);
    return m;
  }, [services]);

  // We still fetch a range, but for month view we need wider bounds.
  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  const dayStart = useMemo(() => {
    const d = new Date(anchor);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);
  const dayEnd = useMemo(() => addDays(dayStart, 1), [dayStart]);

  // Month bounds (nice wide window so calendar month doesn’t “miss” events)
  const monthWindowStart = useMemo(() => {
    const d = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    d.setDate(d.getDate() - 7); // pad
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);

  const monthWindowEnd = useMemo(() => {
    const d = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1);
    d.setDate(d.getDate() + 7); // pad
    d.setHours(0, 0, 0, 0);
    return d;
  }, [anchor]);

  const rangeFrom =
    calView === "day" ? iso(dayStart) : calView === "month" ? iso(monthWindowStart) : iso(weekStart);

  const rangeTo =
    calView === "day" ? iso(dayEnd) : calView === "month" ? iso(monthWindowEnd) : iso(weekEnd);

  const { bookings, loading, error, refresh } = useBookings({
    from: rangeFrom,
    to: rangeTo,
    pollMs: 20000,
  });

  const filteredBookings = useMemo(() => {
    const list = Array.isArray(bookings) ? bookings : [];
    return list.filter((b) => {
      if (statusFilter !== "all" && b.status !== statusFilter) return false;
      if (serviceFilter !== "all" && String(b.service_id) !== String(serviceFilter)) return false;
      return true;
    });
  }, [bookings, statusFilter, serviceFilter]);

  const totals = useMemo(() => {
    let confirmedCents = 0;

    for (const b of filteredBookings) {
      if (b.status !== "confirmed") continue;
      
      // Use actual final price (custom price or discounted price)
      if (b.custom_price_cents) {
        // Custom price was set (overrides everything)
        confirmedCents += Number(b.custom_price_cents);
      } else {
        // Calculate base price then apply discount
        const svc = serviceById.get(String(b.service_id));
        if (!svc) continue;

        const units = Number(b.units || 1);
        let baseCents = 0;
        
        if (svc.pricing_mode === "per_unit") {
          baseCents = Number(svc.price_per_unit_cents || 0) * units;
        } else {
          baseCents = Number(svc.price_cents || 0);
        }
        
        // Apply discount if any
        const discountCents = Number(b.discount_amount_cents || 0);
        const finalCents = baseCents - discountCents;
        
        confirmedCents += Math.max(0, finalCents); // Ensure non-negative
      }
    }

    return { confirmedCents };
  }, [filteredBookings, serviceById]);

  const events = useMemo(() => {
    return filteredBookings
      .map((b) => {
        const start = safeDate(b.start_at);
        const end = safeDate(b.end_at);
        if (!start || !end) return null;

        const svc = serviceById.get(String(b.service_id));
        const units = Number(b.units || 1);

        const unitsSuffix =
          svc && units > 1
            ? ` (${units} ${svc.unit_label || "units"})`
            : units > 1
            ? ` (${units} units)`
            : "";

        // Add discount indicator to title if applicable
        const discountIndicator = b.discount_amount_cents > 0 ? " 💸" : "";
        const title = `${b.customer_name || "Customer"} • ${b.service_name || "Service"}${unitsSuffix}${discountIndicator}`;

        return {
          id: b.id,
          title,
          start,
          end,
          allDay: false,
          resource: b,
        };
      })
      .filter(Boolean);
  }, [filteredBookings, serviceById]);

  // Pass events data to parent for sidebar display
  useEffect(() => {
    onEventsUpdate?.(events, filteredBookings);
  }, [events, filteredBookings, onEventsUpdate]);

  const eventPropGetter = useCallback((event) => {
    const status = event?.resource?.status;
    return { className: eventClassFromStatus(status) };
  }, []);

  return (
    <div className="pb-8">
      {/* Modern Calendar Styling */}
      <style>{`
        .rbc-toolbar {
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
          padding: 0;
          background: transparent;
        }
        .rbc-toolbar-label {
          font-weight: 700;
          font-size: 18px;
          color: #2D2D2D;
        }
        .rbc-btn-group {
          display: flex;
          gap: 4px;
          background: #F5EEDC;
          padding: 4px;
          border-radius: 12px;
        }
        .rbc-btn-group button {
          border-radius: 8px !important;
          padding: 8px 12px !important;
          border: none !important;
          background: transparent !important;
          color: rgba(45, 45, 45, 0.6) !important;
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
        }
        .rbc-btn-group button:hover {
          background: rgba(30, 89, 60, 0.1) !important;
          color: #2D2D2D !important;
        }
        .rbc-btn-group button.rbc-active {
          background: #1E593C !important;
          color: white !important;
          box-shadow: 0 2px 4px rgba(30, 89, 60, 0.2) !important;
        }

        /* Calendar Views */
        .rbc-month-view, .rbc-time-view {
          border-radius: 12px;
          overflow: hidden;
          border: 1px solid rgba(30, 89, 60, 0.15);
          background: white;
        }
        .rbc-header {
          background: #F5EEDC !important;
          border-bottom: 1px solid rgba(30, 89, 60, 0.15) !important;
          padding: 12px 8px !important;
          font-weight: 600 !important;
          color: #2D2D2D !important;
        }
        .rbc-month-row {
          border-bottom: 1px solid rgba(30, 89, 60, 0.1) !important;
        }
        .rbc-date-cell {
          padding: 8px 12px !important;
          border-right: 1px solid rgba(30, 89, 60, 0.1) !important;
        }
        .rbc-today {
          background: #FFF7EB !important;
        }
        .rbc-off-range-bg {
          background: #F5EEDC !important;
        }

        /* Event Styling */
        .rbc-event {
          border: 0 !important;
          border-radius: 8px !important;
          padding: 4px 8px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
          margin: 1px !important;
        }
        .bb-event {
          font-size: 12px;
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .bb-pending {
          background: linear-gradient(135deg, rgba(213, 165, 66, 0.2), rgba(213, 165, 66, 0.35)) !important;
          color: #B88A24 !important;
          border-left: 3px solid #D5A542 !important;
        }
        .bb-confirmed {
          background: linear-gradient(135deg, rgba(30, 89, 60, 0.1), rgba(30, 89, 60, 0.2)) !important;
          color: #1E593C !important;
          border-left: 3px solid #1E593C !important;
        }
        .bb-canceled {
          background: linear-gradient(135deg, #fee2e2, #fecaca) !important;
          color: #991b1b !important;
          text-decoration: line-through;
          border-left: 3px solid #ef4444 !important;
          opacity: 0.8;
        }

        /* Hover Effects */
        .rbc-event:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          transition: all 0.2s ease !important;
        }

        /* Time Grid */
        .rbc-time-slot {
          border-top: 1px solid rgba(30, 89, 60, 0.1) !important;
        }
        .rbc-time-header-content {
          border-left: 1px solid rgba(30, 89, 60, 0.15) !important;
        }
        .rbc-current-time-indicator {
          background-color: #C46A29 !important;
          height: 2px !important;
        }
      `}</style>

      <div className="rounded-xl bg-white border border-birdie-green/15 p-3 sm:p-4">
        <div className="flex items-center justify-between">
          {/* Left: Navigation + Date */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Nav Buttons */}
            <div className="flex items-center">
              <button
                onClick={() => {
                  if (calView === "day") setAnchor(addDays(anchor, -1));
                  else if (calView === "week") setAnchor(addDays(anchor, -7));
                  else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1));
                }}
                className="p-2 text-birdie-charcoal/60 hover:text-birdie-charcoal hover:bg-birdie-beige rounded-md transition-colors"
                title="Previous"
              >
                <ChevronLeftIcon />
              </button>
              <button
                onClick={() => setAnchor(new Date())}
                className="px-2 sm:px-3 py-1.5 text-sm font-medium text-birdie-green hover:bg-birdie-cream rounded-md transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => {
                  if (calView === "day") setAnchor(addDays(anchor, 1));
                  else if (calView === "week") setAnchor(addDays(anchor, 7));
                  else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1));
                }}
                className="p-2 text-birdie-charcoal/60 hover:text-birdie-charcoal hover:bg-birdie-beige rounded-md transition-colors"
                title="Next"
              >
                <ChevronRightIcon />
              </button>
            </div>

            {/* Date Display - Short on mobile, full on desktop */}
            <div className="text-birdie-charcoal font-semibold text-sm sm:text-base">
              {/* Mobile: Short format */}
              <span className="sm:hidden">
                {calView === "day"
                  ? anchor.toLocaleDateString(undefined, { month: "short", day: "numeric" })
                  : calView === "week"
                  ? `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString(undefined, { day: "numeric" })}`
                  : anchor.toLocaleDateString(undefined, { month: "short", year: "2-digit" })}
              </span>
              {/* Desktop: Full format */}
              <span className="hidden sm:inline">
                {calView === "day"
                  ? anchor.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })
                  : calView === "week"
                  ? `${weekStart.toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}`
                  : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}
              </span>
            </div>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={refresh}
              className="p-2 text-birdie-charcoal/60 hover:text-birdie-charcoal hover:bg-birdie-beige rounded-md transition-colors"
              title="Refresh"
            >
              <RefreshIcon />
            </button>
            <button
              onClick={() => {
                setDefaultStart(new Date());
                setCreateOpen(true);
              }}
              className="flex items-center gap-1.5 p-2 sm:px-3 sm:py-1.5 text-sm font-medium text-white bg-birdie-green hover:bg-birdie-green-dark rounded-md transition-colors"
              data-new-booking-btn
              title="New Booking"
            >
              <PlusIcon />
              <span className="hidden sm:inline">New Booking</span>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <EnhancedMiniCard 
          title="Bookings Shown" 
          value={filteredBookings.length} 
          icon={<CalendarEventsIcon />}
          color="blue"
          subtitle="in current view"
        />
        <EnhancedMiniCard 
          title="Revenue (Confirmed)" 
          value={formatUSD(totals.confirmedCents)} 
          icon={<RevenueIcon />}
          color="green"
          subtitle="from visible bookings"
        />
        <EnhancedMiniCard 
          title="Services Active" 
          value={loadingServices ? "…" : services.length} 
          icon={<ServicesIcon />}
          color="purple"
          subtitle="available for booking"
        />
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="mt-6 rounded-2xl border-0 bg-white shadow-lg overflow-hidden">
        {loading && bookings.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Spinner label="Loading calendar..." />
          </div>
        ) : (
          <div className="p-6">
            <Calendar
              localizer={localizer}
              events={events}
              formats={calendarFormats}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 780 }}
              view={calView}
              views={["month", "week", "day", "agenda"]}
              onView={(v) => setCalView(v)}
              date={anchor}
              onNavigate={(d) => setAnchor(d)}
              popup
              selectable
              step={30}
              timeslots={1}
              eventPropGetter={eventPropGetter}
              onSelectEvent={(evt) => setDetail(evt.resource)}
              onSelectSlot={(slot) => {
                setDefaultStart(slot.start);
                setCreateOpen(true);
              }}
            />
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-birdie-charcoal/60">
          <div className="w-2 h-2 bg-birdie-green rounded-full animate-pulse"></div>
          Auto-sync every 20s • Last updated: {new Date().toLocaleTimeString()}
        </div>
        <div className="text-birdie-warmgray">
          💡 Click time slots to create • Click events to edit
        </div>
      </div>

      <EnhancedCreateBookingModal
        isOpen={createOpen}
        onClose={(res) => {
          setCreateOpen(false);
          if (res?.didCreate) refresh();
        }}
        defaultStart={defaultStart}
      />

      <EnhancedBookingDetailModal
        isOpen={!!detail}
        booking={detail}
        onClose={(res) => {
          setDetail(null);
          if (res?.didUpdate) refresh();
        }}
      />
    </div>
  );
}

function EnhancedMiniCard({ title, value, icon, color, subtitle }) {
  const colorClasses = {
    blue: "from-birdie-green to-birdie-green-dark text-white",
    green: "from-birdie-green to-birdie-green-dark text-white",
    purple: "from-birdie-green to-birdie-green-dark text-white",
    amber: "from-birdie-mustard to-birdie-mustard-dark text-white",
  };

  return (
    <div className="group rounded-2xl border-0 bg-white p-6 shadow-lg hover:shadow-xl transition-all duration-200">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="text-sm font-medium text-birdie-charcoal/70 mb-1">{title}</div>
          <div className="text-2xl font-bold text-birdie-charcoal mb-1">{value}</div>
          <div className="text-xs text-birdie-charcoal/60">{subtitle}</div>
        </div>
        <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} shadow-lg group-hover:scale-110 transition-transform duration-200`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

// Calendar-specific Icons
function CalendarEventsIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
      <rect x="7" y="13" width="3" height="3" rx="1"/>
      <rect x="14" y="13" width="3" height="3" rx="1"/>
    </svg>
  );
}

function RevenueIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="1" x2="12" y2="23"/>
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
    </svg>
  );
}

function ServicesIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="15,18 9,12 15,6"/>
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="9,18 15,12 9,6"/>
    </svg>
  );
}

function TodayIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12,6 12,12 16,14"/>
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function RefreshIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M3 21v-5h5"/>
    </svg>
  );
}
